/**
 * @module app.shared
 */

import { isValid, parseISO } from 'date-fns';
import { toDate, format, zonedTimeToUtc } from 'date-fns-tz';
import { mapTimeZone, isTimeZoneUtc, changeLocalTimeZone } from 'app/shared/lib/date-time-core';

export type DateOffsetUnits = 'weeks' | 'days';
export type DateTimeOffsetUnits = 'weeks' | 'days' | 'hours' | 'minutes' | 'seconds' | 'milliseconds';

// compile these because they're going to be used a lot
const isoDateRegex = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const isoUtcDateTimeRegex = /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(?:\.[0-9]+)?Z$/;

export interface ITimeZoneParts {
    timeZone: string,
}

export interface ITimeParts {
    hour: number,
    minute: number,
    second: number,
    millisecond: number
}

export interface IDateParts {
    year: number,
    month: number,
    date: number
}

export interface IDateTimeParts extends IDateParts, ITimeParts { }

export interface IParts extends IDateParts, ITimeParts, ITimeZoneParts { }

abstract class BaseValue<T extends BaseValue<T>> {

    public abstract readonly value: string | null;

    constructor() { }

    public isValid(): boolean {
        return !!this.value;
    }

    public isEqual(other: T) {
        return this.value === other.value;
    }

    public isAfter(other: T) {
        if(!this.value || !other.value) {
            return false;
        }
        return this.value > other.value;
    }

    public isBefore(other: T) {
        if(!this.value || !other.value) {
            return false;
        }
        return this.value < other.value;
    }

    public isBetween(lower: T = this.getMinValue(), upper: T = this.getMaxValue()): boolean {
        if(!this.value || !lower.value || !upper.value) { return false; }

        if(this.value < lower.value) { return false; }
        if(this.value > upper.value) { return false; }
        return true;
    }

    // NOTE [Matt] DateValue and DateTimeValue have no common ancestor. The
    //  isSameDay method relies on an interface that has the toParts(): IParts
    //  method. I could have created a new interface for that,but the interface
    //  between the date values would likely be much more complicated. Rather
    //  adding complexity for this simple change, I've inlied this type instead.
    public isSameDay(other: { toParts(): IParts }) {
        const { year: thisYear, month: thisMonth, date: today } = this.toParts();
        const { year, month, date } = other.toParts();
        return year === thisYear && month === thisMonth && date === today;
    }

    /**
     *
     * @param other The other value
     * @return {number} A signed integer that indicates the relative order of `this` and `other`.  `this` < `other` = -1, `this` == `other` = 0, `this` > `other` = 1
     */
    public compareTo(other: T): number {
        if(this.isBefore(other)) return -1
        else if(this.isEqual(other)) return 0
        else return 1;
    }

    public toJSON(): string | null {
        return this.value;
    }

    public toString(): string {
        return this.value || '';
    }

    public valueOf(): string {
        return this.value || '';
    }

    public abstract toParts(): IParts;

    protected abstract getMinValue(): T;
    protected abstract getMaxValue(): T;

}

export class DateValue extends BaseValue<DateValue> {

    public static readonly INVALID = new DateValue('');
    public static readonly MIN_VALUE = new DateValue('0000-01-01');
    public static readonly MAX_VALUE = new DateValue('9999-12-31');

    public readonly value: string | null;

    constructor(isoDate: string) {
        super();

        this.value = null;

        if(!isoDate) { return; }

        if(isoDateRegex.test(isoDate)) {
            const date = DateValue.parseValue(isoDate);
            if(isValid(date)) {
                this.value = date.toISOString().substr(0, 10);
            }
        }
    }

    public static fromParts(parts: IDateParts): DateValue {
        const dateString = [
            parts.year.toString().padStart(4, '0'),
            '-', parts.month.toString().padStart(2, '0'),
            '-', parts.date.toString().padStart(2, '0')
        ].join('');
        return new DateValue(dateString);
    }

    public static parseIsoDateTime(isoDateTime: string, defaultTimeZone: string): DateValue {
        if(!isoDateTime) { return DateValue.INVALID; }

        // check for yyyy-MM-dd and handle it specially
        if(isoDateRegex.test(isoDateTime)) {
            return new DateValue(isoDateTime);
        }

        // parse ISO dates with possible time zone information
        const date = isTimeZoneUtc(defaultTimeZone) ?
            toDate(isoDateTime) :
            toDate(isoDateTime, { timeZone: mapTimeZone(defaultTimeZone) });

        if(!isValid(date)) { return DateValue.INVALID; }

        return new DateValue(date.toISOString().substr(0, 10));
    }

    public static today(referenceTimeZone: string): DateValue {
        if(!referenceTimeZone) { throw RangeError('Must specify timeZone.'); }
        return DateValue.fromNativeDate(new Date(), referenceTimeZone);
    }

    public static fromNativeDate(date: Date, referenceTimeZone: string): DateValue {
        if(!referenceTimeZone) { throw RangeError('Must specify timeZone.'); }
        if(!isValid(date)) { return DateValue.INVALID; }

        const local = changeLocalTimeZone(date, mapTimeZone(referenceTimeZone));
        const formatted = format(local, 'yyyy-MM-dd');
        return new DateValue(formatted.substr(0, 10));
    }

    public static toNativeDate(date: DateValue, referenceTimeZone: string): Date {
        if(!referenceTimeZone) { throw RangeError('Must specify timeZone.'); }
        if(!date.value) { return new Date(''); }

        if(isTimeZoneUtc(referenceTimeZone)) {
            return toDate(`${date.value}T00:00:00.000Z`);
        }
        return toDate(`${date.value}T00:00:00.000`, { timeZone: mapTimeZone(referenceTimeZone) });
    }

    private static parseValue(value: string): Date {
        return parseISO(`${value}Z`);
    }

    public add(value: number, unit: DateOffsetUnits): DateValue {
        if(!this.value) { return DateValue.INVALID; }

        const baseDate = DateValue.parseValue(this.value);
        switch(unit) {
            case 'weeks':
                baseDate.setUTCDate(baseDate.getUTCDate() + (value * 7));
                break;
            case 'days':
                baseDate.setUTCDate(baseDate.getUTCDate() + value);
                break;
        }

        if(!isValid(baseDate)) {
            return DateValue.INVALID;
        }

        return new DateValue(baseDate.toISOString().substring(0, 10));
    }

    public containsDateTime(dateTime: DateTimeValue, timeZone: string): boolean {
        const today = this.toDateTime(timeZone);
        const tomorrow = this.add(1, 'days').toDateTime(timeZone);
        return dateTime.isBetween(today, tomorrow);
    }

    public toDateTime(time: Partial<ITimeParts> & ITimeZoneParts): DateTimeValue;
    public toDateTime(timeZone: string): DateTimeValue;
    public toDateTime(timeOrTimeZone: string | (Partial<ITimeParts> & ITimeZoneParts)): DateTimeValue {
        const timeZone = typeof timeOrTimeZone === 'string' ? timeOrTimeZone : timeOrTimeZone.timeZone;
        if(!timeZone) { throw RangeError('Must specify timeZone.'); }
        if(!this.value) { return DateTimeValue.INVALID; }

        const time = typeof timeOrTimeZone === 'string' ? { timeZone } : timeOrTimeZone;

        const parts = {
            ...this.toParts(),
            ...{ timeZone },
            ...time
        };

        return DateTimeValue.fromParts(parts);
    }

    public toParts(referenceTimeZone: string = ''): IParts {
        const isValid = !!this.value;
        const value = this.value || '';
        // 0123-56-89
        return {
            timeZone: referenceTimeZone,
            year: isValid ? parseInt(value.substr(0, 4), 10) : 0,
            month: isValid ? parseInt(value.substr(5, 2), 10) : 0,
            date: isValid ? parseInt(value.substr(8, 2), 10) : 0,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0
        };
    }

    protected getMinValue(): DateValue {
        return DateValue.MIN_VALUE;
    }

    protected getMaxValue(): DateValue {
        return DateValue.MAX_VALUE;
    }

}

export class DateTimeValue extends BaseValue<DateTimeValue> {

    public static readonly INVALID = new DateTimeValue('');
    public static readonly MIN_VALUE = new DateTimeValue('0000-01-01T00:00:00.000Z');
    public static readonly MAX_VALUE = new DateTimeValue('9999-12-31T23:59:59.999Z');

    public readonly value: string | null;

    constructor(isoUtcDateTime: string) {
        super();

        this.value = null;

        if(!isoUtcDateTime) { return; }

        if(isoUtcDateTimeRegex.test(isoUtcDateTime)) {
            const date = DateTimeValue.parseValue(isoUtcDateTime);
            if(isValid(date)) {
                this.value = date.toISOString();
            }
        }
    }

    public static fromParts(parts: ITimeZoneParts & IDateParts & Partial<ITimeParts>): DateTimeValue {
        if(!parts.timeZone) { throw RangeError('Must specify timeZone.'); }

        const utc = isTimeZoneUtc(parts.timeZone);
        const dateTimeString = [
            (parts.year || 2000).toString().padStart(4, '0'),
            '-', (parts.month || 1).toString().padStart(2, '0'),
            '-', (parts.date || 1).toString().padStart(2, '0'),
            'T', (parts.hour || 0).toString().padStart(2, '0'),
            ':', (parts.minute || 0).toString().padStart(2, '0'),
            ':', (parts.second || 0).toString().padStart(2, '0'),
            '.', (parts.millisecond || 0).toString().padStart(3, '0'),
            utc ? 'Z' : ''
        ].join('');

        if(utc) {
            return new DateTimeValue(dateTimeString);
        }
        return DateTimeValue.parseIsoDateTime(dateTimeString, parts.timeZone);
    }

    public static parseIsoDateTime(isoDateTime: string, defaultTimeZone?: string): DateTimeValue {
        if(!isoDateTime) { return DateTimeValue.INVALID; }

        // check for yyyy-MM-ddTHH:mm:ss(.SSS)Z and handle it specially
        if(isoUtcDateTimeRegex.test(isoDateTime)) {
            return new DateTimeValue(isoDateTime);
        }

        // parse ISO dates with possible time zone information
        const date = isTimeZoneUtc(defaultTimeZone || '') ?
            toDate(isoDateTime) :
            toDate(isoDateTime, { timeZone: mapTimeZone(defaultTimeZone || '') });

        if(!isValid(date)) { return DateTimeValue.INVALID; }

        return new DateTimeValue(date.toISOString());
    }

    public static now(): DateTimeValue {
        return DateTimeValue.fromNativeDate(new Date());
    }

    public static fromNativeDate(dateTime: Date, localTimeZoneOverride?: string): DateTimeValue {
        if(!isValid(dateTime)) { return DateTimeValue.INVALID; }

        if(localTimeZoneOverride) {
            const utcDateTime = zonedTimeToUtc(dateTime, mapTimeZone(localTimeZoneOverride));
            return new DateTimeValue(utcDateTime.toISOString());
        }
        return new DateTimeValue(dateTime.toISOString());
    }

    public static toNativeDate(dateTime: DateTimeValue, localTimeZoneOverride: string = ''): Date {
        if(!dateTime.value) { return new Date(''); }

        const date = DateTimeValue.parseValue(dateTime.value);
        if(localTimeZoneOverride) {
            return changeLocalTimeZone(date, mapTimeZone(localTimeZoneOverride));
        }
        return date;
    }

    private static parseValue(value: string): Date {
        return parseISO(value);
    }

    public difference(other: DateTimeValue, unit: DateTimeOffsetUnits): number {
        if(!this.value || !other.value) { return Number.NaN; }

        const baseDateTime = DateTimeValue.parseValue(this.value);
        const otherDateTime = DateTimeValue.parseValue(other.value);
        const milliseconds = baseDateTime.getTime() - otherDateTime.getTime();
        switch(unit) {
            case 'days':
                return milliseconds / 1000 / 60 / 60 / 24;
            case 'hours':
                return milliseconds / 1000 / 60 / 60;
            case 'minutes':
                return milliseconds / 1000 / 60;
            case 'seconds':
                return milliseconds / 1000;
            case 'milliseconds':
            default:
                return milliseconds;
        }
    }

    public add(value: number, unit: DateTimeOffsetUnits): DateTimeValue {
        if(!this.value) { return DateTimeValue.INVALID; }

        const baseDateTime = DateTimeValue.parseValue(this.value);
        switch(unit) {
            case 'weeks':
                baseDateTime.setUTCDate(baseDateTime.getUTCDate() + (value * 7));
                break;
            case 'days':
                baseDateTime.setUTCDate(baseDateTime.getUTCDate() + value);
                break;
            case 'hours':
                baseDateTime.setUTCHours(baseDateTime.getUTCHours() + value);
                break;
            case 'minutes':
                baseDateTime.setUTCMinutes(baseDateTime.getUTCMinutes() + value);
                break;
            case 'seconds':
                baseDateTime.setUTCSeconds(baseDateTime.getUTCSeconds() + value);
                break;
            case 'milliseconds':
                baseDateTime.setUTCMilliseconds(baseDateTime.getUTCMilliseconds() + value);
                break;
        }

        if(!isValid(baseDateTime)) {
            return DateTimeValue.INVALID;
        }

        return new DateTimeValue(baseDateTime.toISOString());
    }

    public changeDate(date: DateValue, timeZone: string): DateTimeValue {
        if(!timeZone) { throw RangeError('Must specify timeZone.'); }
        if(!this.value || !date.value) { return this; }

        // swap out the date value and keep the current time part
        const dateParts = date.toParts(timeZone);

        // leave off time zone indicator and pass in timeZone so that parseIsoDateTime can do the timezone conversion
        const utcTime = DateTimeValue.parseValue(this.value);
        const localTime = changeLocalTimeZone(utcTime, mapTimeZone(timeZone));
        localTime.setFullYear(dateParts.year);
        localTime.setMonth(dateParts.month - 1);
        localTime.setDate(dateParts.date);
        const newUtcTime = zonedTimeToUtc(localTime, mapTimeZone(timeZone));

        return DateTimeValue.fromNativeDate(newUtcTime);
    }

    public toDate(timeZone: string): DateValue {
        if(!timeZone) { throw RangeError('Must specify timeZone.'); }
        if(!this.value) { return DateValue.INVALID; }

        const dateTime = toDate(this.value);
        const zonedDate = changeLocalTimeZone(dateTime, mapTimeZone(timeZone));
        // convert zonedDate to a localeDate without time info, otherwise ISO string conversion might jump days due to change from UTC
        const localeDateString = zonedDate.toLocaleDateString();
        const localeDate = new Date(localeDateString);
        return new DateValue(localeDate.toISOString().substr(0, 10));
    }

    public toParts(timeZone: string = 'UTC'): IParts {
        const isValid = !!this.value;
        const value = this.value || '';
        if(isTimeZoneUtc(timeZone) || !isValid) {
            // 0         1         2
            // 0123-56-89T12:45:78.012Z
            return {
                timeZone: timeZone,
                year: isValid ? parseInt(value.substr(0, 4), 10) : 0,
                month: isValid ? parseInt(value.substr(5, 2), 10) : 0,
                date: isValid ? parseInt(value.substr(8, 2), 10) : 0,
                hour: isValid ? parseInt(value.substr(11, 2), 10) : 0,
                minute: isValid ? parseInt(value.substr(14, 2), 10) : 0,
                second: isValid ? parseInt(value.substr(17, 2), 10) : 0,
                millisecond: isValid ? parseInt(value.substr(20, 3), 10) : 0
            };
        }
        else {
            const utcDate = DateTimeValue.parseValue(value);
            const localDate = changeLocalTimeZone(utcDate, mapTimeZone(timeZone));
            return {
                timeZone: timeZone,
                year: localDate.getFullYear(),
                month: localDate.getMonth() + 1, // sigh, why is month 0-based when date isn't?
                date: localDate.getDate(),
                hour: localDate.getHours(),
                minute: localDate.getMinutes(),
                second: localDate.getSeconds(),
                millisecond: localDate.getMilliseconds()
            };
        }
    }

    protected getMinValue(): DateTimeValue {
        return DateTimeValue.MIN_VALUE;
    }

    protected getMaxValue(): DateTimeValue {
        return DateTimeValue.MAX_VALUE;
    }

}
